import { Component, Element, h, Prop, Watch, State, Listen, Event, EventEmitter, Host } from "@stencil/core";
import { AspectRatio } from "../../common/types";
import { ImageLocalization, ImageBackgroundSize } from "./constants/image-types";
import pfx from "../../utils/style-class-prefix-helper";
import { openModal } from "../../utils/openModal";
import { getValueForScreenSize } from "../../utils/screen-helper";
import { LinkTarget } from "../link/constants/link-types";
import { TARGET_SELF } from "../link/constants/link-constants";
import { LocalizableComponent, Localization, setCurrentLocalizationDelegate } from "../../common/localizable-component";
import { computeLinkAriaLabel } from "../../utils/link-helper";
import { IMAGE_LOCALIZATION } from "./constants/image-i18n";
import { ConditionalWrapper } from "../../common/conditional-wrapper";
import { getFirstNestedChild } from "../../utils/dom-helper";
import { setCssVariable } from "../../utils/css-variables";
import {
  ImageViolatorVerticalPosition,
  ImageViolatorHorizontalPosition,
  IMAGE_DEFAULT_VIOLATOR_VERTICAL_POSITION,
  IMAGE_DEFAULT_VIOLATOR_HORIZONTAL_POSITION
} from "../../constants/global-constants";

/**
 * @slot violator - Holds a single eon-ui-violator element
 */
@Component({
  tag: "eon-ui-image",
  styleUrl: "image.scss",
  assetsDirs: ["assets-image"],
  shadow: true
})
export class Image implements LocalizableComponent<ImageLocalization> {
  resizeTimeout: NodeJS.Timeout;
  resizeThrottleMs = 300;

  private dimensionInSrcPath: boolean = false;
  private srcDimension;
  private srcMobileDimension;
  private srcTabletDimension;
  private srcDesktopDimension;
  private srcDesktopLDimension;
  private placeHolderDimensionWidth;
  private placeHolderDimensionHeight;
  private hasViolator: boolean;

  @State() private objectPosition: string;
  @State() private isVisible = false;

  /**
   * Caption can be different in each screen size. This holds the current one.
   */
  @State() private finalCaption: string;

  /**
   * Alt attribute can be different in each screen size. This holds the current one.
   */
  @State() private finalAlt: string;

  /**
   * Title attribute can be different in each screen size. This holds the current one.
   */
  @State() private finalTitle: string;

  /**
   * Title attribute can be different in each screen size. This holds the current one.
   */
  @State() private finalWidth: string;

  localization: Localization<ImageLocalization> = IMAGE_LOCALIZATION;
  currentLocalization: ImageLocalization;

  private currentAriaLabel: string;

  @Element() hostElement: HTMLEonUiImageElement;

  /**
   * Defines vertical position of the violator
   */
  // eslint-disable-next-line @stencil-community/decorators-style
  @Prop({ mutable: true })
  violatorVerticalPosition: ImageViolatorVerticalPosition = IMAGE_DEFAULT_VIOLATOR_VERTICAL_POSITION;

  /**
   * Defines horizontal position of the violator
   */
  // eslint-disable-next-line @stencil-community/decorators-style
  @Prop({ mutable: true })
  violatorHorizontalPosition: ImageViolatorHorizontalPosition = IMAGE_DEFAULT_VIOLATOR_HORIZONTAL_POSITION;

  /**
   * Defines the image source that should be displayed on the desktop-large viewport.
   */
  @Prop() srcDesktopL: string;

  /**
   * Defines the image source that should be displayed on the desktop viewport.
   */
  @Prop() srcDesktop: string;

  /**
   * Defines the image source that should be displayed on the tablet viewport.
   */
  @Prop() srcTablet: string;

  /**
   * Defines the image source that should be displayed on the mobile/phone viewport.
   */
  @Prop() srcMobile: string;

  /**
   * Defines the original image source that is used by the image. Use this if you
   * don't need src values dependant on the screen size. If any of the size-aware
   * src values is not set, the image will fall to the value of "src".
   */
  @Prop() src: string;

  /**
   * Defines the value of the alt attribute for the image passed to "src" (default one).
   */
  @Prop() alt: string;

  /**
   * Defines the alt that should be displayed on the desktop-large viewport.
   */
  @Prop() altDesktopL: string;

  /**
   * Defines the alt that should be displayed on the desktop viewport.
   */
  @Prop() altDesktop: string;

  /**
   * Defines the alt that should be displayed on the tablet viewport.
   */
  @Prop() altTablet: string;

  /**
   * Defines the alt that should be displayed on the mobile/phone viewport.
   */
  @Prop() altMobile: string;

  /**
   * Defines the width of the image. If not set, image width sticks to the format of the image on the desktop-large viewport.
   * Image can adapt full width of the container it is placed in, Only when value of width is "100%"
   * @example "200px", "100%"
   */
  @Prop({ mutable: true }) widthDesktopL: string;

  /**
   * Defines the width of the image. If not set, image width sticks to the format of the image on the desktop viewport.
   * Image can adapt full width of the container it is placed in, Only when value of width is "100%"
   * @example "200px", "100%"
   */
  @Prop({ mutable: true }) widthDesktop: string;

  /**
   * Defines the width of the image. If not set, image width sticks to the format of the image on the tablet viewport.
   * Image can adapt full width of the container it is placed in, Only when value of width is "100%"
   * @example "200px", "100%"
   */
  @Prop({ mutable: true }) widthTablet: string;

  /**
   * Defines the width of the image. If not set, image width sticks to the format of the image on the mobile/phone viewport.
   * Image can adapt full width of the container it is placed in, Only when value of width is "100%"
   * @example "200px", "100%"
   */
  @Prop({ mutable: true }) widthMobile: string;

  /**
   * Defines the width of the image. If not set, image width sticks to the format of the image.
   * Image can adapt full width of the container it is placed in, Only when value of width is "100%"
   * @example "200px", "100%"
   */
  @Prop({ mutable: true }) width: string = "100%";

  /**
   * Defines the height of the image. If not set, image height sticks to the format of the image.
   * Image can adapt full height of the container it is placed inside, Only when value of height is "auto"
   * @example "200px", "100px"
   */
  @Prop() height: string;

  /**
   * Defines the horizontal image position.
   * @example "left", "center", "right", "30px"
   * @default "center"
   */
  @Prop({ mutable: true }) positionX: string = "center";

  /**
   * Defines the vertical image position.
   * @example "top", "center", "bottom", "30px"
   * @default "center"
   */
  @Prop({ mutable: true }) positionY: string = "center";

  /**
   * Set this to true if the image will be used as a full size background image of the parent div.
   */
  @Prop({ reflect: true }) useAsBackground: boolean;

  /**
   * If image is used in background mode you can define if css background-size "cover" or "contain" should be set.
   */
  @Prop({ reflect: true, mutable: true }) objectFit: ImageBackgroundSize;

  /**
   * Defines the aspect ratio of the image.
   */
  @Prop() aspectRatio: AspectRatio;

  /**
   * Defines the caption for the image passed to "src" (default one).
   */
  @Prop() caption: string;

  /**
   * Defines the caption that should be displayed on the desktop-large viewport.
   */
  @Prop() captionDesktopL: string;

  /**
   * Defines the caption that should be displayed on the desktop viewport.
   */
  @Prop() captionDesktop: string;

  /**
   * Defines the caption that should be displayed on the tablet viewport.
   */
  @Prop() captionTablet: string;

  /**
   * Defines the caption that should be displayed on the mobile/phone viewport.
   */
  @Prop() captionMobile: string;

  /**
   * Allows to set a copyright text for caption component.
   */
  @Prop() captionCopyright: string;

  /**
   * If this is set, copywrite will come in a new line beneath the caption at the defined pixel value. By that it is possible to control line wrap independently of breakpoint, but based on the image width.
   */
  @Prop() captionVerticalBreakpoint: number;

  /**
   * If enabled, the image will be loaded lazily when it's visible in the viewport. Defaults to false.
   */
  @Prop({ mutable: true }) lazy: boolean = false;

  /**
   * rootMargin: the image will be loaded lazily when it is "rootMargin" pixels away from entering the viewport.
   * Instead of loading the image just when they exactly enter the viewport,
   * load the images when they are, suppose 300px away from entering the viewport.
   * This provides additional time, between the load trigger and the actual entry in the viewport, for the images to load.
   */
  @Prop({ mutable: true }) rootMargin: string = "100";

  /**
   * If enabled, the image is overlaid with a 20% opacity E.ON darkgrey tint.
   */
  @Prop({ mutable: true }) shadowOnImage: boolean = false;

  /**
   * If enabled, the image is overlaid with a 40% opacity E.ON white tint.
   */
  @Prop({ mutable: true }) brightShadowOnImage: boolean = false;

  /**
   * use this property to open a modal, the modal must have the same modal-id<br>
   * see component: Modal
   */

  @Prop() openModalId: string;

  /**
   * Defines the value of the title attribute for the image passed to "title" (default one).
   */
  @Prop() titleDefault: string;

  /**
   * Defines the title that should be displayed on the desktop-large viewport
   */
  @Prop() titleDesktopL: string;

  /**
   * Defines the title that should be displayed on the desktop viewport.
   */
  @Prop() titleDesktop: string;

  /**
   * Defines the title that should be displayed on the tablet viewport
   */
  @Prop() titleTablet: string;

  /**
   * Defines the title that should be displayed on the mobile/phone viewport
   */
  @Prop() titleMobile: string;

  /**
   * Specifies the label to use for accessibility.
   * If not set a computed aria label will set e.g. "navigate to..". It depends on the closet lang attribute-
   */
  @Prop() linkAriaLabel: string;

  /**
   * Defines the target url of a image.
   * Valid protocols are "http://", "https://", "mailto:" and "tel:".
   */
  @Prop() linkHref: string;

  /**
   * Specifies how the link is opened in the browser, e.g. in new window/tab.
   * @example "_blank", "_self", "_parent", "_top"
   * @default "_self"
   */
  @Prop({ mutable: true }) linkTarget: LinkTarget = TARGET_SELF;

  /**
   * this property sets the title of the link
   */
  @Prop({ mutable: true }) linkTitle: string;

  /**
   * this property disable the context menu of this image (disable right click, dragging, mousedown on image)
   */
  @Prop() preventUserInteraction: boolean = false;

  /**
   * Triggered when the component has been loaded
   */
  @Event() imageLoaded: EventEmitter;

  /**
   * Triggered when the image src has been loaded
   */
  @Event() imageSrcLoaded: EventEmitter;

  /**
   * Raised when the image has been clicked.
   */
  @Event() imageClick: EventEmitter;

  /**
   * If defined, component will parse image src strings based on the given character to get dimensions.
   * sizes should be available in the following format with a flexible character inbetween: [WIDTH][CHARACTER][HEIGHT].
   * e.g. 1024x768, 1024.768
   * \d{1,}\x\d{1,}
   */
  @Prop() imageDimensionSplitCharacter: string;

  componentWillLoad() {
    this.setCurrentLocalization();
    this.computeObjectPosition();
    this.imageLazyLoading(this.hostElement, `${this.rootMargin}px`);
    this.setCaption();
    this.setAlt();
    this.setTitle();
    this.setWidth();
    this.computeAriaLabel();
    this.handleImageDimensionsIfPossible();
    this.hasViolator = !!getFirstNestedChild(this.hostElement, "eon-ui-violator");
  }

  componentDidLoad() {
    this.setPlaceHolderDimensionCssVars();
    this.imageLoaded.emit();
    this.removeContextMenu();
  }

  @Watch("positionX")
  @Watch("positionY")
  private computeObjectPosition() {
    const objectPositionX: string = this.positionX ? this.positionX : "center";
    const objectPositionY: string = this.positionY ? this.positionY : "center";
    this.objectPosition = `${objectPositionX} ${objectPositionY}`;
  }

  @Watch("preventUserInteraction")
  removeContextMenu() {
    if (!this.preventUserInteraction) {
      this.hostElement.removeEventListener("contextmenu", (event) => {
        this.preventUserInteractionOnImage(event);
      });
      this.hostElement.removeEventListener("mousedown", (event) => {
        this.preventUserInteractionOnImage(event);
      });
    } else {
      this.hostElement.addEventListener("contextmenu", (event) => {
        this.preventUserInteractionOnImage(event);
      });
      this.hostElement.addEventListener("mousedown", (event) => {
        this.preventUserInteractionOnImage(event);
      });
    }
  }

  setCurrentLocalization() {
    setCurrentLocalizationDelegate(this);
  }

  @Listen("resize", { target: "window" })
  handleResize() {
    if (!this.resizeTimeout) {
      this.resizeTimeout = setTimeout(() => {
        this.setCaption();
        this.setAlt();
        this.setTitle();
        this.setWidth();
        this.resizeTimeout = null;
        if (this.dimensionInSrcPath) this.setPlaceHolderDimension();
        this.setPlaceHolderDimensionCssVars();
      }, this.resizeThrottleMs);
    }
  }

  preventUserInteractionOnImage(event: Event) {
    if (this.preventUserInteraction) event.preventDefault();
  }

  handleImageDimensionsIfPossible() {
    if (this.imageDimensionSplitCharacter) {
      this.getImageDimensionsFromSource();
      if (this.dimensionInSrcPath) this.setPlaceHolderDimension();
    }
  }

  private extractImageDimensionFromSource(src: string) {
    let dimension;
    let patterNumber = "\\d{1,}";
    let patterSeparator = `\\${this.imageDimensionSplitCharacter}`;
    let rx = new RegExp(`${patterNumber}${patterSeparator}${patterNumber}`, "g");
    let dimensionString = rx.exec(src);

    if (dimensionString) {
      // get last image dimension available in string
      let tempDimensionArr = dimensionString[dimensionString.length - 1].split(this.imageDimensionSplitCharacter);

      dimension = {
        width: tempDimensionArr[0],
        height: tempDimensionArr[1]
      };
    }

    return dimension;
  }

  private getImageDimensionsFromSource() {
    this.src && (this.srcDimension = this.extractImageDimensionFromSource(this.src));
    this.srcMobile && (this.srcMobileDimension = this.extractImageDimensionFromSource(this.srcMobile));
    this.srcTablet && (this.srcTabletDimension = this.extractImageDimensionFromSource(this.srcTablet));
    this.srcDesktop && (this.srcDesktopDimension = this.extractImageDimensionFromSource(this.srcDesktop));
    this.srcDesktopL && (this.srcDesktopLDimension = this.extractImageDimensionFromSource(this.srcDesktopL));

    if (this.srcDimension) this.dimensionInSrcPath = true;
  }

  private setPlaceHolderDimension() {
    this.placeHolderDimensionWidth = getValueForScreenSize(this.srcDimension?.width, {
      mobile: this.srcMobileDimension?.width,
      tablet: this.srcTabletDimension?.width,
      desktop: this.srcDesktopDimension?.width,
      desktopL: this.srcDesktopLDimension?.width
    });

    this.placeHolderDimensionHeight = getValueForScreenSize(this.srcDimension?.height, {
      mobile: this.srcMobileDimension?.height,
      tablet: this.srcTabletDimension?.height,
      desktop: this.srcDesktopDimension?.height,
      desktopL: this.srcDesktopLDimension?.height
    });
  }

  private setPlaceHolderDimensionCssVars() {
    if (this.placeHolderDimensionWidth && this.placeHolderDimensionHeight) {
      setCssVariable(this.hostElement, `--placeholderDimensionWidth`, `${this.placeHolderDimensionWidth}`);
      setCssVariable(this.hostElement, `--placeholderDimensionHeight`, `${this.placeHolderDimensionHeight}`);
    }
  }

  private imageLazyLoading(element: HTMLElement, rootMargin: string) {
    if (this.lazy && window && window.IntersectionObserver) {
      const imageObserver = new window.IntersectionObserver(
        (entries: IntersectionObserverEntry[]) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              let image = entry.target;
              this.isVisible = true;
              imageObserver.unobserve(image);
            }
          });
        },
        { rootMargin }
      );
      imageObserver.observe(element);
    }
  }

  handleClick(_event: Event) {
    if (!!this.openModalId) {
      openModal(this.openModalId);
    }
    this.imageClick.emit();
  }

  @Watch("caption")
  @Watch("captionMobile")
  @Watch("captionTablet")
  @Watch("captionDesktop")
  @Watch("captionDesktopL")
  private setCaption() {
    this.finalCaption = getValueForScreenSize(this.caption, {
      mobile: this.captionMobile,
      tablet: this.captionTablet,
      desktop: this.captionDesktop,
      desktopL: this.captionDesktopL
    });
  }

  @Watch("alt")
  @Watch("altMobile")
  @Watch("altTablet")
  @Watch("altDesktop")
  @Watch("altDesktopL")
  private setAlt() {
    this.finalAlt = getValueForScreenSize(this.alt, {
      mobile: this.altMobile,
      tablet: this.altTablet,
      desktop: this.altDesktop,
      desktopL: this.altDesktopL
    });
  }

  @Watch("titleDefault")
  @Watch("titleMobile")
  @Watch("titleTablet")
  @Watch("titleDesktop")
  @Watch("titleDesktopL")
  private setTitle() {
    this.finalTitle = getValueForScreenSize(this.titleDefault, {
      mobile: this.titleMobile,
      tablet: this.titleTablet,
      desktop: this.titleDesktop,
      desktopL: this.titleDesktopL
    });
  }

  @Watch("width")
  @Watch("widthMobile")
  @Watch("widthTablet")
  @Watch("widthDesktop")
  @Watch("widthDesktopL")
  private setWidth() {
    this.finalWidth = getValueForScreenSize(this.width, {
      mobile: this.widthMobile,
      tablet: this.widthTablet,
      desktop: this.widthDesktop,
      desktopL: this.widthDesktopL
    });
  }

  @Watch("linkHref")
  private computeAriaLabel() {
    if (this.linkAriaLabel || !this.currentLocalization) {
      this.currentAriaLabel = this.linkAriaLabel;
      return;
    }
    this.currentAriaLabel = computeLinkAriaLabel(this.currentLocalization, this.linkHref, null);
  }

  render() {
    let autoWidth: boolean = Boolean(this.finalWidth === "auto" || !this.finalWidth);
    let isCover: boolean = Boolean(this.width && this.height);
    let isPlaceholder: boolean =
      this.imageDimensionSplitCharacter &&
      this.lazy &&
      !this.aspectRatio &&
      !this.useAsBackground &&
      this.dimensionInSrcPath;
    let isFixedRatio: boolean = Boolean(this.aspectRatio) || isPlaceholder;
    let isBackground: boolean = Boolean(this.useAsBackground);
    let ariaLabel = this.linkAriaLabel || this.currentAriaLabel;
    let hostWidth;

    // This calculation takes the sizes of the dimensionInSrcPath anytime they are available.
    // Nevertheless, that causes issues when a ratio (2x1) is provided instead of an actual image size.
    if (!isBackground) {
      if (autoWidth) {
        if (this.dimensionInSrcPath) {
          hostWidth = this.placeHolderDimensionWidth + "px";
        } else if (this.aspectRatio) {
          hostWidth = "100%";
        }
      } else {
        hostWidth = this.finalWidth;
      }
    }

    return (
      <Host style={{ width: hostWidth }}>
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
        <div class={pfx({ "image-caption-container": true })} onClick={(event: MouseEvent) => this.handleClick(event)}>
          <div
            class={pfx({
              ["image-container"]: true,
              ["cover"]: isCover,
              ["background"]: isBackground,
              ["fixedRatio"]: isFixedRatio && !isBackground,
              [`${this.aspectRatio}`]: isFixedRatio && !isBackground,
              ["placeholder"]: isPlaceholder && !isBackground
            })}
            style={{
              height: isBackground ? "" : this.height
            }}
          >
            <ConditionalWrapper
              condition={!!this.linkHref && !this.useAsBackground}
              wrapper={(imageTag) => (
                <a title={this.linkTitle} href={this.linkHref} target={this.linkTarget} aria-label={ariaLabel}>
                  {imageTag}
                </a>
              )}
              defaultWrapper={(imageTag) => imageTag}
            >
              {/*{this is the image tag}*/}
              <figure>
                <picture>
                  {(!this.lazy || this.isVisible) && this.srcDesktopL ? (
                    <source srcSet={this.srcDesktopL} media="(min-width: 1536px)" />
                  ) : null}
                  {(!this.lazy || this.isVisible) && (this.srcDesktop || this.src) ? (
                    <source srcSet={this.srcDesktop || this.src} media="(min-width: 1024px) AND (max-width: 1535px)" />
                  ) : null}
                  {(!this.lazy || this.isVisible) && this.srcTablet ? (
                    <source srcSet={this.srcTablet} media="(min-width: 768px) AND (max-width: 1023px)" />
                  ) : null}
                  {(!this.lazy || this.isVisible) && this.srcMobile ? (
                    <source srcSet={this.srcMobile} media="(max-width: 767px)" />
                  ) : null}
                  <img
                    src={(!this.lazy || this.isVisible) && this.src ? this.src : null}
                    alt={this.finalAlt}
                    title={this.finalTitle}
                    style={{
                      "object-position": this.objectPosition,
                      "object-fit": isCover || isBackground ? this.objectFit : "",
                      height: isCover || isBackground ? "" : this.height
                    }}
                    onLoad={() => this.imageSrcLoaded.emit()}
                  />
                </picture>
              </figure>
              {this.shadowOnImage && <div class={pfx("image-shadow-overlay")}></div>}
              {this.brightShadowOnImage && <div class={pfx("image-shadow-overlay-bright")}></div>}
            </ConditionalWrapper>
            {this.hasViolator && (
              <div
                class={pfx({
                  "image-violator": true,
                  [`v-${this.violatorVerticalPosition}`]: true,
                  [this.violatorHorizontalPosition]: !!this.violatorHorizontalPosition
                })}
              >
                <slot name="violator"></slot>
              </div>
            )}
          </div>
          {!isBackground && (this.finalCaption || this.captionCopyright) && (
            <eon-ui-caption
              caption={this.finalCaption}
              copyright={this.captionCopyright}
              vertical-breakpoint-width={this.captionVerticalBreakpoint}
            ></eon-ui-caption>
          )}
        </div>
      </Host>
    );
  }
}
